/**
 * Lot & Elevation DOM Component (Housing Unit)
 *
 * Renders the Lot & Elevation content panel for housing unit detail, including
 * Home Features when present.
 *
 * @module nativeInteractiveSiteMap/components/lotAndElevationHousingUnit.dom
 */

import fastdom from 'fastdom';
import getLotGroupCardHtml from './lotGroupCard.dom';
import getLotHomeFeaturesCardHtml from './lotHomeFeature.dom';

/**
 * Creates the Lot & Elevation content component for housing unit detail.
 * @param {Object} detailItem - The currently selected housing unit
 * @param {Object} lot - The currently selected lot
 * @returns {{ getElement: Function, remove: Function }}
 */
export default function createLotAndElevationHousingUnitDom(detailItem = {}, lot = {}) {
  const state = { root: null };

  function initializeDom() {
    state.root = document.createElement('div');
    state.root.className = [
      'nhs-c-native-interactive-site-map__lot-elevation',
    ].join(' ');

    // Use backend-formatted presentation field for consistency with formattedPrice.
    const size = lot?.formattedLotSize ?? null;
    const imageSrc = detailItem?.image || null; // Use actual image URL if available
    const name = detailItem?.name || null; // Use actual name if available
    const lotNumber = lot?.displayLabel ? `Lot ${lot.displayLabel}` : null;
    const group = lot?.lotGroupName ? `- Group ${lot.lotGroupName}` : null;
    const type = (lot?.lotType && lot.lotType !== 'None') ? lot.lotType : null;
    const facing = (lot?.lotFacing && lot.lotFacing !== 'N/A') ? lot.lotFacing : null;
    const homeFeatures = detailItem?.homeFeatures ?? [];
    const hasHomeFeatures = homeFeatures.length > 0;
    const homeFeaturesForDisplay = hasHomeFeatures ? homeFeatures : [];

    state.root.innerHTML = `
      <div class="nhs-u-padding--0-500 nhs-u-flex-column nhs-u-gap--500">
        <!-- Placeholder image for Lot & Elevation -->

        <div class="nhs-c-lotelevation__image-wrapper nhs-mq-lotelevation__image-wrapper-large">
          <img data-siteplan-card-image
            class="nhs-c-lotelevation__image"
            src="${imageSrc}"
            alt="${name}">
        </div>

        <!-- Lot Details Card -->
        ${getLotGroupCardHtml({ lotNumber, group, size, type, facing })}

        ${hasHomeFeatures ? '<div class="nhs-c-lot-home-features-divider nhs-u-width--full"></div>' : ''}

        <!-- Home Features Card -->
        ${getLotHomeFeaturesCardHtml(homeFeaturesForDisplay)}


        <div class="nhs-u-border-top--1 nhs-u-border-color--neutral-300 nhs-u-padding-top--100"></div>
      </div>
    `;
  }

  initializeDom();

  return {
    getElement() {
      return state.root;
    },

    remove() {
      fastdom.mutate(() => {
        state.root?.remove();
        state.root = null;
      });
    },
  };
}
